import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { ModelListItemFragment } from "@/apollo/types";

import { usePublishedModels } from "./PublishedModelsProvider";
import { useRawViews } from "./RawViewsProvider";

type ReferenceMapValueType =
  | ModelListItemFragment["dwSync"]
  | ModelListItemFragment["dwTable"]
  | ReturnType<typeof useRawViews>["rawViews"][number];

type ReferenceMapType = Map<string, ReferenceMapValueType>;

const ReferenceMapContext = createContext<ReferenceMapType | null>(null);

type ReferenceMapGetter = () => ReferenceMapType;
const ReferenceMapGetterContext = createContext<ReferenceMapGetter | null>(
  null,
);

export function ModelDependenciesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const models = usePublishedModels().publishedModels;
  const rawViews = useRawViews().rawViews;

  const map = useMemo(() => {
    const map = new Map<string, ReferenceMapValueType>();

    for (const rawView of rawViews) {
      map.set(rawView.weldTag, rawView);
    }

    for (const model of models) {
      const dwItem = model.dwSync ?? model.dwTable;
      if (!dwItem) {
        continue;
      }
      if (dwItem.weldTag) {
        map.set(dwItem.weldTag, dwItem);
      }
    }

    return map;
  }, [models, rawViews]);

  // Create a ref to hold the current publishedModels value.
  const modelDependenciesRef = useRef<ReferenceMapType>(map);

  // Update the ref whenever the publishedModels part of our state changes.
  useEffect(() => {
    modelDependenciesRef.current = map;
  }, [map]);

  // Stable getter function that always returns the latest published models.
  const getMap = useCallback(() => {
    return modelDependenciesRef.current;
  }, []);

  return (
    <ReferenceMapContext.Provider value={map}>
      <ReferenceMapGetterContext.Provider value={getMap}>
        {children}
      </ReferenceMapGetterContext.Provider>
    </ReferenceMapContext.Provider>
  );
}

export const useModelDependenciesMap = () => {
  const context = useContext(ReferenceMapContext);
  if (!context) {
    throw new Error(
      "useModelDependenciesMap must be used within a ReferenceMapContext",
    );
  }
  return context;
};

export const useModelDepedenciesMapGetter = () => {
  const getMap = useContext(ReferenceMapGetterContext);
  if (!getMap) {
    throw new Error(
      "useModelDepedenciesMapGetter must be used within a ReferenceMapGetterContext",
    );
  }
  return getMap;
};
